import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from "@/api/base44Client";
import { DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import useEntityTagging, { EMPTY_TAGS } from '@/components/hooks/useEntityTagging';
import EntityTagInput from '@/components/common/EntityTagInput';

export default function ReportActivityIssueModal({ activity, open, onClose, officers, artifacts, rituals, policies, capabilities, initialData }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState(null);

  useEffect(() => {
    base44.auth.me().then(user => setCurrentUserEmail(user?.email)).catch(() => {});
  }, []);

  // Use the unified tagging hook
  const tagging = useEntityTagging({
    officers,
    artifacts,
    rituals,
    policies,
    capabilities,
    currentUserEmail,
    initialTags: null
  });

  // Reset form when modal opens with initial data
  useEffect(() => {
    if (open && initialData) {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      if (initialData.tags) {
        tagging.setTags(prev => ({ ...prev, ...initialData.tags }));
      }
    }
  }, [open, initialData]);

  const createIssueMutation = useMutation({
    mutationFn: async (issueData) => {
      const user = await base44.auth.me();
      const normalizeText = (t) => (t || '').trim().toLowerCase().replace(/\s+/g, ' ');

      // Duplicate detection: check for matching issues within a 6-hour window
      // This catches duplicates across pathways (activity direct + progress report automation)
      const existingIssues = await base44.entities.Issue.list();
      const now = new Date();
      const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);
      const newTitle = normalizeText(issueData.title);
      const newDesc = normalizeText(issueData.description);

      const duplicate = existingIssues.find(existing => {
        if (existing.linked_activity_id !== activity.id) return false;
        const existingDate = new Date(existing.created_date || existing.reported_at);
        if (existingDate < sixHoursAgo) return false;
        const exTitle = normalizeText(existing.title);
        const exDesc = normalizeText(existing.description);
        if (exTitle === newTitle && newTitle.length > 5) return true;
        if (exDesc === newDesc && newDesc.length > 5) return true;
        if (newDesc.length > 15 && exDesc.length > 15) {
          if (exDesc.includes(newDesc) || newDesc.includes(exDesc)) return true;
        }
        if (newTitle.length > 15 && exTitle.length > 15) {
          if (exTitle.includes(newTitle) || newTitle.includes(exTitle)) return true;
        }
        // Cross-check: title vs description (progress report issues use description as title)
        if (newTitle.length > 15 && exDesc.length > 15) {
          if (exDesc.includes(newTitle) || newTitle.includes(exDesc)) return true;
        }
        if (newDesc.length > 15 && exTitle.length > 15) {
          if (exTitle.includes(newDesc) || newDesc.includes(exTitle)) return true;
        }
        return false;
      });

      if (duplicate) {
        throw new Error('A similar issue was already reported for this activity recently. Duplicate skipped.');
      }

      // All timestamps in UTC ISO format
      return base44.entities.Issue.create({
        ...issueData,
        reported_by: user.email,
        reported_at: new Date().toISOString(),
        status: 'open',
        category: 'execution_headwind',
        severity: 'medium',
        linked_activity_id: activity.id,
        linked_objective_id: activity.objective_id,
        source: initialData?.source || 'activity',
        source_id: initialData?.source_id || activity.id
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      handleClose();
    }
  });

  const handleClose = () => {
    setTitle('');
    setDescription('');
    tagging.resetTags();
    onClose();
  };

  const handleSubmit = () => {
    if (!title.trim()) return;
    
    createIssueMutation.mutate({
      title,
      description,
      ...tagging.tags
    });
  };

  return (
    <ConfirmableDialog open={open} onOpenChange={(val) => !val && handleClose()}>
      <DialogContent data-testid="civ-reportactivityissuemodal-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle>Report Issue</DialogTitle>
          <DialogDescription className="text-slate-400">
            Report a problem or issue blocking this activity.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Issue Title *</Label>
            <Input data-testid="civ-reportactivityissuemodal-input" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder="Brief title of the issue"
              className="bg-slate-950 border-slate-700"
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea data-testid="civ-reportactivityissuemodal-textarea" 
              value={description} 
              onChange={(e) => setDescription(e.target.value)} 
              placeholder="Describe the issue (max 500 chars)..."
              maxLength={500}
              className="bg-slate-950 border-slate-700 min-h-[100px]"
            />
            <div className="text-xs text-slate-500 text-right">{description.length}/500</div>
          </div>

          <div className="space-y-2">
            <Label>Tags</Label>
            <EntityTagInput
              tags={tagging.tags}
              onAddTag={tagging.addTag}
              onRemoveTag={tagging.removeTag}
              getTagOptions={tagging.getTagOptions}
              currentTagType={tagging.currentTagType}
              onTagTypeChange={tagging.setCurrentTagType}
              officers={officers}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} className="text-slate-400 hover:text-white" data-testid="civ-cancel-btn">Cancel</Button>
          <Button data-testid="civ-submit-issue-btn" onClick={handleSubmit} disabled={!title.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Submit Issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </ConfirmableDialog>
  );
}